import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, ShoppingBag, X } from "lucide-react";
import { Logo } from "./Logo";
import { useCart } from "../lib/cart";
import { useCurrency } from "../lib/currency";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/products", label: "Products" },
  { to: "/about", label: "About Us" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const { count, setOpen } = useCart();
  const { currency, toggle } = useCurrency();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/80 backdrop-blur-md">
      <nav className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 lg:px-8">
        <Link to="/" data-testid="nav-logo-link" aria-label="OneCare Pharmacy home">
          <Logo />
        </Link>

        <div className="hidden items-center gap-1 md:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}-link`}
              className={({ isActive }) =>
                `rounded-full px-4 py-2 text-sm font-medium transition-[background-color,color] duration-200 ${
                  isActive ? "bg-emerald-50 text-emerald-800" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="currency-toggle"
            onClick={toggle}
            aria-label="Switch currency between US dollars and British pounds"
            className="flex items-center rounded-full border border-slate-200 bg-white p-1 font-mono text-[11px] font-bold transition-[border-color] hover:border-emerald-600/40"
          >
            <span className={`rounded-full px-2.5 py-1 transition-[background-color,color] ${currency === "USD" ? "bg-[#0D5C46] text-white" : "text-slate-500"}`}>
              $ USD
            </span>
            <span className={`rounded-full px-2.5 py-1 transition-[background-color,color] ${currency === "GBP" ? "bg-[#0D5C46] text-white" : "text-slate-500"}`}>
              £ GBP
            </span>
          </button>
          <button
            type="button"
            data-testid="nav-cart-button"
            aria-label="Open enquiry basket"
            onClick={() => setOpen(true)}
            className="relative rounded-full border border-slate-200 bg-white p-2.5 text-slate-700 transition-[border-color,color,transform] hover:border-emerald-600/40 hover:text-emerald-700 active:scale-95"
          >
            <ShoppingBag className="h-5 w-5" />
            {count > 0 && (
              <span data-testid="nav-cart-count" className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#0D5C46] px-1 font-mono text-[10px] font-bold text-white">
                {count}
              </span>
            )}
          </button>
          <Link
            to="/contact"
            data-testid="nav-enquiry-cta"
            className="hidden rounded-full bg-[#0D5C46] px-5 py-2.5 text-sm font-semibold text-white transition-[background-color,transform] hover:bg-[#0A4837] active:scale-95 md:block"
          >
            Make an Enquiry
          </Link>
          <button
            type="button"
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            onClick={() => setMobileOpen((o) => !o)}
            className="rounded-full p-2.5 text-slate-700 md:hidden"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-slate-100 bg-white md:hidden"
          >
            <div className="space-y-1 px-5 py-4">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s/g, "-")}-link`}
                  className={({ isActive }) =>
                    `block rounded-lg px-4 py-3 text-sm font-medium ${
                      isActive ? "bg-emerald-50 text-emerald-800" : "text-slate-700"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
